<div class='row'>
  <div class='col-md-6'>
    <div class='sheet'>
      <h4 class='header'>Stock Share by Warehouse</h4>
      <p class='text-muted mb-2'>
        Share of total quantity on hand. Negative totals cannot be part of a whole and are
        reported in the alert above rather than plotted.
      </p>

      <!-- A pie only earns its place as a genuine part-to-whole with enough segments. Two
           slices is a decorated pair of numbers and one slice is a filled circle, so below
           the floor this falls back to the figures themselves. -->
      @if (this.dashboard.stockShareByWarehouse.renderable) {
        <div class='chart-holder'>
          <ngx-charts-pie-chart
            [view]='this.view'
            [scheme]='this.colorScheme'
            [customColors]='this.dashboard.stockShareByWarehouse.customColors'
            [results]='this.dashboard.stockShareByWarehouse.data'
            [gradient]='this.gradient'
            [legend]='this.showLegend'
            [legendPosition]='this.legendPosition'
            [labels]='this.showPieLabels'
            [doughnut]='this.isDoughnut'
            [animations]='this.animations'>
          </ngx-charts-pie-chart>
        </div>
      }

      @if (!this.dashboard.stockShareByWarehouse.renderable) {
        <p class='text-muted'><em>{{ this.dashboard.stockShareByWarehouse.suppressedBecause }}</em></p>
      }

      @if (this.dashboard.stockShareByWarehouse.renderable && this.dashboard.stockShareByWarehouse.suppressedBecause) {
        <p class='text-danger mb-2'><small>{{ this.dashboard.stockShareByWarehouse.suppressedBecause }}</small></p>
      }

      <!-- Table twin. Every charted value stays readable as text, never only as a hue. -->
      @if (this.dashboard.stockShareByWarehouse.data.length > 0) {
        <table class='table table-sm chart-table mb-0'>
          <thead>
            <tr>
              <th>Warehouse</th>
              <th class='text-right'>Qty</th>
              <th class='text-right'>Share</th>
            </tr>
          </thead>
          <tbody>
            @for (row of this.dashboard.stockShareByWarehouse.data; track row.name) {
              <tr>
                <td>
                  <span class='series-chip'
                    [style.background-color]='this.colorFor(this.dashboard.stockShareByWarehouse, row.name)'></span>
                  {{ this.fullLabel(this.dashboard.stockShareByWarehouse, row.name) }}
                </td>
                <td class='numeric'>{{ this.formatQty(row.value) }}</td>
                <td class='numeric'>{{ this.sharePercent(row.value) }}</td>
              </tr>
            }
            <!-- Deliberately "Total charted" and not "Total". This is the sum of the
                 positive shares only, so it does not equal the Total Stock Qty tile above
                 whenever a site is negative. Two figures both labelled "total" that disagree
                 would make a reader distrust both, so the difference is named. -->
            <tr>
              <td><strong>Total charted</strong></td>
              <td class='numeric'><strong>{{ this.formatQty(this.stockShareTotal) }}</strong></td>
              <td class='numeric'>100%</td>
            </tr>
            @if (this.dashboard.stockShareByWarehouse.suppressedBecause) {
              <tr>
                <td colspan='3'>
                  <small class='text-muted'>
                    Excludes any site at a negative total, so this differs from the
                    Total Stock Qty figure above.
                  </small>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>

  <div class='col-md-6'>
    <div class='sheet'>
      <h4 class='header'>Top Products by Quantity</h4>
      <p class='text-muted mb-2'>
        Quantity on hand per product, largest first. One series, so one colour — the bar
        length already carries the size. A product owing stock is shown in the alert colour.
      </p>

      @if (this.dashboard.topProductsByQty.renderable) {
        <div class='chart-holder'>
          <ngx-charts-bar-horizontal
            [view]='this.view'
            [scheme]='this.colorScheme'
            [customColors]='this.dashboard.topProductsByQty.customColors'
            [results]='this.dashboard.topProductsByQty.data'
            [gradient]='this.gradient'
            [xAxis]='this.showXAxis'
            [yAxis]='this.showYAxis'
            [legend]='false'
            [showXAxisLabel]='this.showXAxisLabel'
            [showYAxisLabel]='this.showYAxisLabel'
            [xAxisLabel]='this.qtyAxisLabel'
            [yAxisLabel]='this.productAxisLabel'
            [roundEdges]='this.roundEdges'
            [animations]='this.animations'>
          </ngx-charts-bar-horizontal>
        </div>
      }

      @if (!this.dashboard.topProductsByQty.renderable) {
        <p class='text-muted'><em>{{ this.dashboard.topProductsByQty.suppressedBecause }}</em></p>
      }

      @if (this.dashboard.topProductsByQty.data.length > 0) {
        <table class='table table-sm chart-table mb-0'>
          <thead>
            <tr>
              <th>Product</th>
              <th class='text-right'>Qty on hand</th>
            </tr>
          </thead>
          <tbody>
            @for (row of this.dashboard.topProductsByQty.data; track row.name) {
              <tr>
                <td>{{ row.name }}</td>
                <td class='numeric' [class.text-danger]='this.isNegative(row.value)'>
                  {{ this.formatQty(row.value) }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>
</div>

<div class='row'>
  <div class='col-md-7'>
    <div class='sheet'>
      <h4 class='header'>Movements by Type</h4>
      <p class='text-muted mb-2'>
        Movements posted in the last {{ this.dashboard.movementWindowDays }} days, stacked by
        warehouse. Counting movements rather than summing quantity, because a transfer's
        matched pair would cancel to zero and read as nothing having happened.
      </p>

      @if (this.dashboard.movementsByType.renderable) {
        <div class='chart-holder chart-holder--tall'>
          <ngx-charts-bar-vertical-stacked
            [view]='this.view'
            [scheme]='this.colorScheme'
            [customColors]='this.dashboard.movementsByType.customColors'
            [results]='this.dashboard.movementsByType.data'
            [gradient]='this.gradient'
            [xAxis]='this.showXAxis'
            [yAxis]='this.showYAxis'
            [legend]='this.showLegend'
            [showXAxisLabel]='this.showXAxisLabel'
            [showYAxisLabel]='this.showYAxisLabel'
            [xAxisLabel]='this.movementTypeAxisLabel'
            [yAxisLabel]='this.movementCountAxisLabel'
            [yAxisTickFormatting]='this.wholeNumberTick'
            [animations]='this.animations'>
          </ngx-charts-bar-vertical-stacked>
        </div>
      }

      @if (!this.dashboard.movementsByType.renderable) {
        <p class='text-muted'><em>{{ this.dashboard.movementsByType.suppressedBecause }}</em></p>
      }

      @if (this.dashboard.movementsByType.data.length > 0) {
        <table class='table table-sm chart-table mb-0'>
          <thead>
            <tr>
              <th>Type</th>
              @for (site of this.dashboard.movementsByType.data[0].series; track site.name) {
                <th class='text-right'>
                  <span class='series-chip'
                    [style.background-color]='this.colorFor(this.dashboard.movementsByType, site.name)'></span>
                  {{ this.fullLabel(this.dashboard.movementsByType, site.name) }}
                </th>
              }
              <th class='text-right'>Total</th>
            </tr>
          </thead>
          <tbody>
            @for (group of this.dashboard.movementsByType.data; track group.name) {
              <tr>
                <td>{{ group.name }}</td>
                @for (site of group.series; track site.name) {
                  <td class='numeric'>{{ site.value }}</td>
                }
                <td class='numeric'><strong>{{ this.groupTotal(group) }}</strong></td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>

  <div class='col-md-5'>
    <div class='sheet'>
      <h4 class='header'>Movements per Day</h4>
      <p class='text-muted mb-2'>
        Movement volume over the last {{ this.dashboard.movementWindowDays }} days.
      </p>

      <!-- One bar is a number pretending to be a trend, so below two days this is suppressed
           and the Movements Today tile carries it instead. -->
      @if (this.dashboard.movementsByDay.renderable) {
        <div class='chart-holder'>
          <ngx-charts-bar-vertical
            [view]='this.view'
            [scheme]='this.colorScheme'
            [customColors]='this.dashboard.movementsByDay.customColors'
            [results]='this.dashboard.movementsByDay.data'
            [gradient]='this.gradient'
            [xAxis]='this.showXAxis'
            [yAxis]='this.showYAxis'
            [legend]='false'
            [showXAxisLabel]='this.showXAxisLabel'
            [showYAxisLabel]='this.showYAxisLabel'
            [xAxisLabel]='this.dayAxisLabel'
            [yAxisLabel]='this.movementCountAxisLabel'
            [yAxisTickFormatting]='this.wholeNumberTick'
            [roundEdges]='this.roundEdges'
            [animations]='this.animations'>
          </ngx-charts-bar-vertical>
        </div>
      }

      @if (!this.dashboard.movementsByDay.renderable) {
        <p class='text-muted'><em>{{ this.dashboard.movementsByDay.suppressedBecause }}</em></p>
      }

      @if (this.dashboard.movementsByDay.data.length > 0) {
        <table class='table table-sm chart-table mb-0'>
          <thead>
            <tr>
              <th>Day</th>
              <th class='text-right'>Movements</th>
            </tr>
          </thead>
          <tbody>
            @for (row of this.dashboard.movementsByDay.data; track row.name) {
              <tr>
                <td>{{ row.name }}</td>
                <td class='numeric'>{{ row.value }}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>
</div>
